import { shallowRef } from 'vue';
import { useRouter } from 'vue-router';
import { useAuthStore } from '@/stores/auth';
import { useUiStore } from '@/stores/ui';
import { useSettingsStore } from '@/stores/settings';
import { setBrowseHandoff, handoffFromItem } from '@/helpers/browse-handoff';
import { canUseTitlePreviewRoute, parseTitlePreview, TITLE_PREVIEW_QUERY } from '@/helpers/title-preview';

const hover = shallowRef(null);
const seed = shallowRef(null);
const origin = shallowRef(null);
let hoverTimer;
let closeTimer;
let hoverVersion = 0;
let pendingOwner;

export function canAutoPreview() {
  return typeof window !== 'undefined'
    && !document.hidden
    && window.matchMedia('(hover: hover) and (pointer: fine)').matches
    && !window.matchMedia('(prefers-reduced-motion: reduce)').matches
    && !navigator.connection?.saveData;
}

export function useTitlePreview() {
  const router = useRouter();
  const auth = useAuthStore();
  const ui = useUiStore();
  const settings = useSettingsStore();

  function eligible(target) {
    if (!auth.canBrowseTorrents || ui.tvMode || !canUseTitlePreviewRoute(router.currentRoute.value)) return null;
    const resolved = router.resolve(target);
    if (resolved.name !== 'browse-media') return null;
    return parseTitlePreview(`${resolved.params.type}-${resolved.params.id}`);
  }

  function hideHover(owner) {
    if (owner && (hover.value?.owner || pendingOwner) !== owner) return;
    ++hoverVersion;
    clearTimeout(hoverTimer);
    clearTimeout(closeTimer);
    hoverTimer = null;
    pendingOwner = null;
    closeTimer = null;
    hover.value = null;
  }

  function keepHover() {
    clearTimeout(closeTimer);
    closeTimer = null;
  }

  function leaveHover(owner) {
    if (owner && (hover.value?.owner || pendingOwner) !== owner) return;
    clearTimeout(hoverTimer);
    hoverTimer = null;
    if (owner && hover.value?.owner !== owner) return;
    keepHover();
    closeTimer = setTimeout(() => hideHover(owner), 180);
  }

  function hoverTitle(item, target, owner, { pointer = true } = {}) {
    const identity = eligible(target);
    if (!identity || !owner || parseTitlePreview(router.currentRoute.value.query[TITLE_PREVIEW_QUERY])
      || !window.matchMedia('(min-width: 600px) and (hover: hover) and (pointer: fine)').matches) return;
    hideHover();
    pendingOwner = owner;
    const version = hoverVersion;
    hoverTimer = setTimeout(async () => {
      hoverTimer = null;
      if (version !== hoverVersion || !owner.isConnected || document.hidden) return;
      const rect = owner.getBoundingClientRect();
      hover.value = { ...identity, owner, rect, item: { ...item }, details: null, target, pointer };
      try {
        const { getMediaDetails } = await import('@/services/tmdb');
        if (version !== hoverVersion || hover.value?.owner !== owner) return;
        const details = await getMediaDetails(identity.id, identity.mediaType, settings.language, settings.region,
          { includeProviders: false, priority: -5 });
        if (version !== hoverVersion || hover.value?.owner !== owner) return;
        hover.value = { ...hover.value, details };
      } catch { /* keep the card's artwork and full-detail link available */ }
    }, 500);
  }

  function openTitle(item, target, owner) {
    const identity = eligible(target);
    if (!identity) return false;
    const current = router.currentRoute.value;
    const replacing = !!parseTitlePreview(current.query[TITLE_PREVIEW_QUERY]);
    if (!replacing) {
      const el = owner || hover.value?.owner;
      const rect = (el?.querySelector?.('.tv-poster-card__art') || el)?.getBoundingClientRect?.();
      origin.value = { element: el?.querySelector?.('a') || el, rect: rect ? { left: rect.left, top: rect.top, width: rect.width, height: rect.height } : null };
    }
    seed.value = { ...identity, item: { ...item } };
    setBrowseHandoff(identity.mediaType, identity.id, handoffFromItem(item));
    hideHover();
    const query = { ...current.query, [TITLE_PREVIEW_QUERY]: identity.key };
    const baseQuery = { ...current.query };
    delete baseQuery[TITLE_PREVIEW_QUERY];
    const background = router.resolve({ path: current.path, query: baseQuery, hash: current.hash }).fullPath;
    const next = { path: current.path, query, hash: current.hash, state: { titlePreviewFrom: background } };
    router[replacing ? 'replace' : 'push'](next).catch(() => {});
    return true;
  }

  function closeTitle() {
    const current = router.currentRoute.value;
    if (!parseTitlePreview(current.query[TITLE_PREVIEW_QUERY])) return;
    const query = { ...current.query };
    delete query[TITLE_PREVIEW_QUERY];
    const base = router.resolve({ path: current.path, query, hash: current.hash });
    if (window.history.state?.titlePreviewFrom === base.fullPath) router.back();
    else router.replace({ path: current.path, query, hash: current.hash }).catch(() => {});
  }

  return { hover, seed, origin, hoverTitle, leaveHover, keepHover, hideHover, openTitle, closeTitle };
}
